import {
  Col,
  Row,
  Tabs,
  Table,
  Typography,
  Spin,
  Empty,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Card,
  Radio,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDate, formatDateOnly } from '../../helpers/date';
import moment from 'moment';
import useAxiosPost from '../../hooks/useAxiosPost';
import SingleSiteCard from '../Items/Cards/site/SingleSiteCard';
import useMultiNotification from '../../hooks/useNotification';
import VoucherStatsChart from './VoucherStatsChart';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import '../Items/Table/index.css';
import './singleSiteComponent.css';

interface RouterTableDataType {
  intPkSiteCoreRouterID: number;
  routerName: string;
  coreRouterIp: string;
  coreRouterVendorName: string;
  onlineUsersCount?: number;
  key?: string;
  enable?: number;
}

const SingleSiteComponent = () => {
  const { intPkCompanySiteID } = useParams();
  const navigate = useNavigate();
  const { openNotification, contextHolder } = useMultiNotification();

  const [siteDetails, setSiteDetails] = useState<SingleSiteCardProp>({
    enable: false,
    dateCreated: '',
    siteName: '',
    siteAddress: '',
    intPkCompanySiteID: 0,
    intPkCompanyID: 0,
    routerDetails: {
      coreRouterIp: '',
      coreRouterVendorName: '',
      eUserLocalNetworkLinkLogin: '',
      intPkCoreRouterVendorsID: '',
      intPkSiteCoreRouterID: '',
      routerName: '',
    },
  });

  const [routers, setRouters] = useState<RouterTableDataType[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUserTableDataType[]>([]);
  const [voucherStats, setVoucherStats] = useState<Array<{ date: string; count: number }>>([]);
  const [connectedUsersStatsPeriod, setConnectedUsersStatsPeriod] = useState<
    'daily' | 'yesterday' | 'weekly' | 'monthly'
  >('daily');
  const [connectedUsersStatsData, setConnectedUsersStatsData] = useState<any>(null);
  const [form] = Form.useForm();
  const [isCreateRouterModalOpen, setIsCreateRouterModalOpen] = useState(false);
  const [vendors, setVendors] = useState<any[]>([]);
  const postRoutersApiDataRef = useRef<any>(null);
  const hasProcessedCreateRouterSuccess = useRef(false);

  const [postApiData, apiResponse, apiError, apiLoading, apiMessage, apiErrorCount] =
    useAxiosPost<ResponseProp>('company-sites/details');

  const [
    postRoutersApiData,
    routersApiResponse,
    routersApiError,
    routersApiLoading,
    routersApiMessage,
    routersApiErrorCount,
  ] = useAxiosPost<ResponseProp>('routers/all');

  const [
    postOnlineUsersApiData,
    onlineUsersApiResponse,
    onlineUsersApiError,
    onlineUsersApiLoading,
    onlineUsersApiMessage,
    onlineUsersApiErrorCount,
  ] = useAxiosPost<ResponseProp>('company-sites/online-users');

  const [
    postVoucherStatsApiData,
    voucherStatsApiResponse,
    voucherStatsApiError,
    voucherStatsApiLoading,
    voucherStatsApiMessage,
    voucherStatsApiErrorCount,
  ] = useAxiosPost<ResponseProp>('vouchers/site-stats');

  const [
    postConnectedUsersStatsApiData,
    connectedUsersStatsApiResponse,
    connectedUsersStatsApiError,
    connectedUsersStatsApiLoading,
    connectedUsersStatsApiMessage,
    connectedUsersStatsApiErrorCount,
  ] = useAxiosPost<ResponseProp>('company-sites/site-connected-users-stats');

  useEffect(() => {
    postRoutersApiDataRef.current = postRoutersApiData;
  }, [postRoutersApiData]);

  useEffect(() => {
    if (intPkCompanySiteID) {
      postApiData({ id: Number(intPkCompanySiteID) } as any);
      postRoutersApiData({
        intPkCompanySiteID: Number(intPkCompanySiteID),
      } as any);
      postOnlineUsersApiData({ id: Number(intPkCompanySiteID) } as any);
      postVoucherStatsApiData({ intPkCompanySiteID: Number(intPkCompanySiteID) } as any);
      postConnectedUsersStatsApiData({ id: Number(intPkCompanySiteID), period: connectedUsersStatsPeriod } as any);
    }
  }, [intPkCompanySiteID]);

  useEffect(() => {
    if (intPkCompanySiteID && connectedUsersStatsPeriod) {
      postConnectedUsersStatsApiData({ id: Number(intPkCompanySiteID), period: connectedUsersStatsPeriod } as any);
    }
  }, [connectedUsersStatsPeriod, intPkCompanySiteID]);

  useEffect(() => {
    if (routersApiResponse !== null) {
      const responseData: any = routersApiResponse.responseData;
      if (Array.isArray(responseData)) {
        const routersWithKeys = responseData.map((router: any) => ({
          intPkSiteCoreRouterID: router.intPkSiteCoreRouterID,
          routerName: router.routerName || '',
          coreRouterIp: router.coreRouterIp || '',
          coreRouterVendorName: router.coreRouterVendorName || '',
          onlineUsersCount: router.onlineUsersCount ?? 0,
          enable: router.enable,
          key: router.intPkSiteCoreRouterID.toString(),
        }));
        setRouters(routersWithKeys);
      }
    }
  }, [routersApiResponse]);

  useEffect(() => {
    if (onlineUsersApiResponse !== null) {
      const responseData: any = onlineUsersApiResponse.responseData;
      if (Array.isArray(responseData)) {
        setOnlineUsers(responseData);
      }
    }
  }, [onlineUsersApiResponse]);

  useEffect(() => {
    if (voucherStatsApiResponse !== null) {
      const responseData: any = voucherStatsApiResponse.responseData;
      if (responseData && responseData.stats && Array.isArray(responseData.stats)) {
        setVoucherStats(responseData.stats);
      }
    }
  }, [voucherStatsApiResponse]);

  useEffect(() => {
    if (connectedUsersStatsApiResponse !== null) {
      const responseData: any = connectedUsersStatsApiResponse.responseData;
      setConnectedUsersStatsData(responseData);
    }
  }, [connectedUsersStatsApiResponse]);

  const refreshSiteDetails = () => {
    if (intPkCompanySiteID) {
      postApiData({ id: Number(intPkCompanySiteID) } as any);
    }
  };

  useEffect(() => {
    if (apiResponse !== null) {
      const responseData: any = apiResponse.responseData;

      const { dateCreated, enable, intPkCompanyID, intPkCompanySiteID, siteAddress, siteName } = responseData;

      const _dateCreated = formatDateOnly(dateCreated).toString();

      setSiteDetails({
        enable: Boolean(Number(enable)),
        dateCreated: _dateCreated,
        intPkCompanyID: Number(intPkCompanyID),
        intPkCompanySiteID: Number(intPkCompanySiteID),
        siteAddress: siteAddress,
        siteName: siteName,
        routerDetails: null,
      });
    }
  }, [apiResponse, routers]);

  const routerColumns: ColumnsType<RouterTableDataType> = [
    {
      title: 'Router Name',
      dataIndex: 'routerName',
      key: 'routerName',
      render: (text: string) => <Typography.Text strong>{text || '-'}</Typography.Text>,
    },
    {
      title: 'IP Address',
      dataIndex: 'coreRouterIp',
      key: 'coreRouterIp',
      render: (text: string) => text || '-',
    },
    {
      title: 'Vendor',
      dataIndex: 'coreRouterVendorName',
      key: 'coreRouterVendorName',
      render: (text: string) => text || '-',
    },
    {
      title: 'Status',
      dataIndex: 'enable',
      key: 'enable',
      render: (value: number | undefined) => {
        const isEnabled = value === undefined ? true : Number(value) === 1;
        return <Tag color={isEnabled ? 'success' : 'default'}>{isEnabled ? 'Enabled' : 'Disabled'}</Tag>;
      },
    },
    {
      title: 'Online Users',
      dataIndex: 'onlineUsersCount',
      key: 'onlineUsersCount',
      render: (count: number) => count ?? 0,
      align: 'center',
    },
  ];

  const onlineUsersColumns: ColumnsType<OnlineUserTableDataType> = [
    {
      title: 'Voucher Number',
      dataIndex: 'voucherNumber',
      key: 'voucherNumber',
      render: (text: string) => text || '-',
    },
    {
      title: 'MAC Address',
      dataIndex: 'eUserDevMAC',
      key: 'eUserDevMAC',
      render: (text: string) => text || '-',
    },
    {
      title: 'User IP',
      dataIndex: 'eUserDevIP',
      key: 'eUserDevIP',
      render: (text: string) => text || '-',
    },
    // {
    //   title: 'Session ID',
    //   dataIndex: 'acctSessionID',
    //   key: 'acctSessionID',
    //   render: (text: string) => text || '-',
    // },
    // {
    //   title: 'VLAN',
    //   dataIndex: 'nasPortID',
    //   key: 'nasPortID',
    //   render: (text: string) => text || '-',
    // },
    {
      title: 'Total Upload',
      dataIndex: 'formattedTotalUploadedBytes',
      key: 'formattedTotalUploadedBytes',
      render: (text: string) => text || '0 B',
    },
    {
      title: 'Total Download',
      dataIndex: 'formattedTotalDownloadedBytes',
      key: 'formattedTotalDownloadedBytes',
      render: (text: string) => text || '0 B',
    },
    {
      title: 'Connected Since',
      dataIndex: 'formattedConnectedSince',
      key: 'formattedConnectedSince',
      render: (text: string) => text || '0 seconds',
    },
  ];

  const handleRowClick = (record: RouterTableDataType) => {
    navigate(`/router-details/${record.intPkSiteCoreRouterID}`, {
      state: { intPkCompanySiteID: Number(intPkCompanySiteID) },
    });
  };

  const tabItems = [
    {
      key: 'routers',
      label: 'Routers',
      children: (
        <>
          <div
            style={{
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          ></div>
          <div className="table-container-custom">
            <Table
              className="antd-table-striped router-table-clickable sites-table-clickable"
              columns={routerColumns}
              dataSource={routers}
              pagination={false}
              loading={routersApiLoading}
              rowClassName={(record) =>
                record.enable !== undefined && Number(record.enable) === 0 ? 'router-disabled' : ''
              }
              onRow={(record) => ({
                onClick: () => handleRowClick(record),
                style: { cursor: 'pointer' },
              })}
            />
          </div>
        </>
      ),
    },
    {
      key: 'devices',
      label: 'Online Users',
      children: (
        <>
          {onlineUsersApiLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <Typography style={{ marginTop: '16px', display: 'block' }}>Loading online users...</Typography>
            </div>
          ) : onlineUsersApiError && !onlineUsersApiLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Empty
                description={
                  <Typography.Text type="danger">
                    {onlineUsersApiError || 'Failed to load online users'}
                  </Typography.Text>
                }
              />
            </div>
          ) : onlineUsers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Empty description="No online users found for this site." />
            </div>
          ) : (
            <div className="table-container-custom">
              <Table
                className="antd-table-striped"
                columns={onlineUsersColumns}
                dataSource={onlineUsers}
                pagination={false}
                loading={onlineUsersApiLoading}
                rowKey={(record) => `${record.acctSessionID}-${record.eUserDevMAC}`}
              />
            </div>
          )}
        </>
      ),
    },
    {
      key: 'voucher-stats',
      label: 'Statistics',
      children: (
        <div>
          <VoucherStatsChart
            stats={voucherStats}
            loading={voucherStatsApiLoading}
            error={voucherStatsApiError}
            siteName={siteDetails.siteName}
          />
          <div style={{ marginTop: '24px' }}>
            <SiteConnectedUsersChart
              statsData={connectedUsersStatsData}
              period={connectedUsersStatsPeriod}
              loading={connectedUsersStatsApiLoading}
              error={connectedUsersStatsApiError}
              onPeriodChange={setConnectedUsersStatsPeriod}
            />
          </div>
        </div>
      ),
    },
  ];

  if (apiLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <Typography style={{ marginTop: '16px', display: 'block' }}>Loading site details...</Typography>
      </div>
    );
  }

  if (apiError && !apiLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Empty
          description={<Typography.Text type="danger">{apiError || 'Failed to load site details'}</Typography.Text>}
        />
      </div>
    );
  }

  if (!siteDetails.siteName && !apiLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Empty description="Site details not found" />
      </div>
    );
  }

  return (
    <>
      {contextHolder}

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <SingleSiteCard {...siteDetails} onlineUsersCount={onlineUsers.length} onUpdate={refreshSiteDetails} />
        </Col>
        <Col span={24}>
          <Tabs defaultActiveKey="routers" items={tabItems} />
        </Col>
      </Row>
    </>
  );
};

interface SiteConnectedUsersChartProps {
  statsData: any;
  period: 'daily' | 'yesterday' | 'weekly' | 'monthly';
  loading: boolean;
  error: string | null;
  onPeriodChange: (period: 'daily' | 'yesterday' | 'weekly' | 'monthly') => void;
}

const SiteConnectedUsersChart: React.FC<SiteConnectedUsersChartProps> = ({
  statsData,
  period,
  loading,
  error,
  onPeriodChange,
}) => {
  const formatDateForDisplay = (dateStr: string, isHour: boolean = false): string => {
    if (!dateStr) return '';
    if (isHour) {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } else {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const day = date.getDate();
      const month = moment(date).format('MMM');
      return `${day} ${month}`;
    }
  };

  // Guard against invalid data
  if (!statsData || !statsData.stats || !Array.isArray(statsData.stats) || statsData.stats.length === 0) {
    if (loading) {
      return (
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <Typography style={{ marginTop: '16px', display: 'block' }}>
              Loading connected users statistics...
            </Typography>
          </div>
        </Card>
      );
    }

    if (error) {
      return (
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Empty
              description={
                <Typography.Text type="danger">{error || 'Failed to load connected users statistics'}</Typography.Text>
              }
            />
          </div>
        </Card>
      );
    }

    return (
      <Card
        title="Site Connected Users Statistics"
        extra={
          <Radio.Group value={period} onChange={(e) => onPeriodChange(e.target.value)} buttonStyle="solid">
            <Radio.Button value="daily">Daily</Radio.Button>
            <Radio.Button value="yesterday">Yesterday</Radio.Button>
            <Radio.Button value="weekly">Weekly</Radio.Button>
            <Radio.Button value="monthly">Monthly</Radio.Button>
          </Radio.Group>
        }
      >
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description="No data available for the chart" />
        </div>
      </Card>
    );
  }

  let categories: string[] = [];
  let seriesData: any[] = [];
  let chartOptions: ApexOptions;

  if (period === 'daily' || period === 'yesterday') {
    const validStats = statsData.stats.filter(
      (item: any) => item && item.hour && item.connectedUsers !== null && item.connectedUsers !== undefined,
    );

    if (validStats.length === 0) {
      return (
        <Card
          title="Site Connected Users Statistics"
          extra={
            <Radio.Group value={period} onChange={(e) => onPeriodChange(e.target.value)} buttonStyle="solid">
              <Radio.Button value="daily">Daily</Radio.Button>
              <Radio.Button value="yesterday">Yesterday</Radio.Button>
              <Radio.Button value="weekly">Weekly</Radio.Button>
              <Radio.Button value="monthly">Monthly</Radio.Button>
            </Radio.Group>
          }
        >
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Empty description="No valid data available for the chart" />
          </div>
        </Card>
      );
    }

    categories = validStats.map((item: any) => formatDateForDisplay(item.hour, true));
    seriesData = [
      {
        name: 'Connected Users',
        data: validStats.map((item: any) => {
          const value = Number(item.connectedUsers);
          return isNaN(value) ? 0 : value;
        }),
      },
    ];

    chartOptions = {
      chart: {
        type: 'line',
        height: 350,
        toolbar: {
          show: false,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          colors: ['#304758'],
        },
      },
      xaxis: {
        categories: categories,
        title: {
          text: 'Time',
          style: {
            fontSize: '14px',
            fontWeight: 600,
          },
        },
      },
      yaxis: {
        title: {
          text: 'Connected Users',
          style: {
            fontSize: '14px',
            fontWeight: 600,
          },
        },
        labels: {
          formatter: (value: number) => {
            return Math.floor(value).toString();
          },
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.25,
          gradientToColors: ['#3A77BC'],
          inverseColors: false,
          opacityFrom: 0.85,
          opacityTo: 0.85,
          stops: [0, 100],
        },
      },
      colors: ['#3A77BC'],
      tooltip: {
        y: {
          formatter: (value: number) => {
            return `${value} user${value !== 1 ? 's' : ''}`;
          },
        },
      },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 3,
      },
    };
  } else {
    const validStats = statsData.stats.filter(
      (item: any) =>
        item &&
        item.date &&
        item.minConnectedUsers !== null &&
        item.minConnectedUsers !== undefined &&
        item.maxConnectedUsers !== null &&
        item.maxConnectedUsers !== undefined,
    );

    if (validStats.length === 0) {
      return (
        <Card
          title="Site Connected Users Statistics"
          extra={
            <Radio.Group value={period} onChange={(e) => onPeriodChange(e.target.value)} buttonStyle="solid">
              <Radio.Button value="daily">Daily</Radio.Button>
              <Radio.Button value="yesterday">Yesterday</Radio.Button>
              <Radio.Button value="weekly">Weekly</Radio.Button>
              <Radio.Button value="monthly">Monthly</Radio.Button>
            </Radio.Group>
          }
        >
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Empty description="No valid data available for the chart" />
          </div>
        </Card>
      );
    }

    categories = validStats.map((item: any) => formatDateForDisplay(item.date, false));
    seriesData = [
      {
        name: 'Minimum Connected Users',
        data: validStats.map((item: any) => {
          const value = Number(item.minConnectedUsers);
          return isNaN(value) ? 0 : value;
        }),
        color: '#ff4d4f',
      },
      {
        name: 'Maximum Connected Users',
        data: validStats.map((item: any) => {
          const value = Number(item.maxConnectedUsers);
          return isNaN(value) ? 0 : value;
        }),
        color: '#52c41a',
      },
    ];

    chartOptions = {
      chart: {
        type: 'bar',
        height: 350,
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: false,
          columnWidth: '55%',
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: '12px',
          colors: ['#304758'],
        },
      },
      xaxis: {
        categories: categories,
        title: {
          text: 'Date',
          style: {
            fontSize: '14px',
            fontWeight: 600,
          },
        },
      },
      yaxis: {
        title: {
          text: 'Connected Users',
          style: {
            fontSize: '14px',
            fontWeight: 600,
          },
        },
        labels: {
          formatter: (value: number) => {
            return Math.floor(value).toString();
          },
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.25,
          gradientToColors: ['#3A77BC'],
          inverseColors: false,
          opacityFrom: 0.85,
          opacityTo: 0.85,
          stops: [0, 100],
        },
      },
      colors: ['#ff4d4f', '#52c41a'],
      tooltip: {
        y: {
          formatter: (value: number) => {
            return `${value} user${value !== 1 ? 's' : ''}`;
          },
        },
      },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 3,
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
      },
    };
  }

  // Ensure we have valid data before rendering
  if (categories.length === 0 || seriesData.length === 0 || seriesData[0].data.length === 0) {
    return (
      <Card
        title="Site Connected Users Statistics"
        extra={
          <Radio.Group value={period} onChange={(e) => onPeriodChange(e.target.value)} buttonStyle="solid">
            <Radio.Button value="daily">Daily</Radio.Button>
            <Radio.Button value="yesterday">Yesterday</Radio.Button>
            <Radio.Button value="weekly">Weekly</Radio.Button>
            <Radio.Button value="monthly">Monthly</Radio.Button>
          </Radio.Group>
        }
      >
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description="No data available for the chart" />
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Site Connected Users Statistics"
      extra={
        <Radio.Group value={period} onChange={(e) => onPeriodChange(e.target.value)} buttonStyle="solid">
          <Radio.Button value="daily">Daily</Radio.Button>
          <Radio.Button value="yesterday">Yesterday</Radio.Button>
          <Radio.Button value="weekly">Weekly</Radio.Button>
          <Radio.Button value="monthly">Monthly</Radio.Button>
        </Radio.Group>
      }
    >
      {/* Min/Max Labels for Daily and Yesterday */}
      {(period === 'daily' || period === 'yesterday') &&
        statsData.minConnectedUsers !== null &&
        statsData.maxConnectedUsers !== null && (
          <div style={{ marginBottom: '16px', display: 'flex', gap: '24px', justifyContent: 'center' }}>
            <Typography.Text strong style={{ color: '#52c41a' }}>
              Maximum: {statsData.maxConnectedUsers} users
            </Typography.Text>
            <Typography.Text strong style={{ color: '#ff4d4f' }}>
              Minimum: {statsData.minConnectedUsers} users
            </Typography.Text>
          </div>
        )}
      <ReactApexChart
        options={chartOptions}
        series={seriesData}
        type={period === 'daily' || period === 'yesterday' ? 'line' : 'bar'}
        height={350}
      />
    </Card>
  );
};

export default SingleSiteComponent;
